<template>
  <v-container ma-0 pa-0>
    <vue-recaptcha
        v-if="config.recaptchaEnabled"
        ref="invisibleRecaptcha"
        @verify="handleLogin"
        @error="showError"
        @expired="onExpired"
        size="invisible"
        :sitekey="recaptchaSiteKey()">
    </vue-recaptcha>
    <div class="text-h5 mb-10 secondary--text">
      {{$_t('Sign In')}}
    </div>
    <div class="ma-0 pa-0">
      <v-form @submit.prevent="handleSubmit('login-form')" data-vv-scope="login-form">
        <v-text-field
            v-validate="'required|min:4|max:100'"
            :error-messages="vErrors.collect('login-form.identity')"
            data-vv-name="identity"
            v-model="username"
            name="email"
            :label="$_t('Email address')"
            color="primary"
            outlined
            :placeholder="$_t('Enter email')"
            persistent-placeholder
            :disabled="status.loginRequest || formLoading"
            tabindex="1"
            :autofocus="autofocus === 'username'"
            autocomplete="username"
        ></v-text-field>

        <v-text-field
            v-validate="'required|min:' + config.passwordMinLength + '|max:30'"
            :error-messages="vErrors.collect('login-form.password')"
            data-vv-name="password"
            ref="password"
            :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-outline'"
            v-model="password"
            name="password"
            :label="$_t('Password')"
            :type="showPassword ? 'text' : 'password'"
            color="primary"
            light
            outlined
            :placeholder="$_t('Enter password')"
            persistent-placeholder
            :disabled="status.loginRequest || formLoading"
            tabindex="2"
            :autofocus="autofocus === 'password'"
            autocomplete="current-password"
            @click:append="showPassword = !showPassword"
        ></v-text-field>
          <router-link
            :to="'reset-password'"
            tabindex="4"
            v-if="config.passwordResetEnabled"
            class="text-decoration-none float-end text-caption"
          >
            {{$_t('Forgot your password?')}}
          </router-link>

          <v-btn
              tabindex="3"
              type="submit"
              large
              class="mt-12"
              :loading="status.loginRequest"
              color="primary"
              light
              :disabled="formLoading"
              block
          >
              {{ $_t('Login') }}
          </v-btn>
      </v-form>
    </div>

    <div class="pa-4">
      <div class="text-subtitle-2 mb-4 mt-xs-0 mt-md-16 secondary--text">{{$_t("Don't have an account?")}}</div>
      <p class="text-caption text--secondary">
        {{ $_t('We invite radiologists and medical facilities that perform X-ray examinations to cooperate with us. We reduce costs by 40%.')}}
      </p>
      <a :href="config.contactUsLink" target="_blank" class="text-subtitle-2 text-decoration-none">
        <v-icon class="float-left" color="primary">mdi-email-outline</v-icon>
        <div class="float-left ml-1" style="line-height: 24px">{{ $_t('Contact us') }}</div>
      </a>
    </div>
    <div>
      <SocialLogin :disabled="!formLoading"/>
    </div>
  </v-container>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import config from '@/config';
import VueRecaptcha from 'vue-recaptcha'
import SocialLogin from './../../SocialLogin'
import device from '@/helpers/device'

export default {
  components: {
    VueRecaptcha,
    SocialLogin
  },
  props: {
    isAdminLogin: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      username: '',
      password: '',
      code: '',
      config: {'logoFull': '#'},
      rememberMe: false,
      type: 'native',
      autofocus: 'username',
      showPassword: false
    }
  },
  computed: {
    ...mapState('account', ['status', 'formLoading']),
    dynamicWidth: function() {
      return this.$vuetify.breakpoint.xsOnly ? {maxWidth: '330px'} : {};
    }
  },
  created () {
    this.isRecaptchaIPWhitelisted();
  },
  methods: {
    ...mapActions('account', ['loginRequest', 'submitUserIdentity', 'isRecaptchaIPWhitelisted']),
    ...mapActions('alert', ['error']),
    handleSubmit(scope) {
      this.$validator.validateAll(scope)
        .then((valid) => {
          if (valid) {
            this.loginRequest();
            if (this.isRecaptchaEnabled() && !device.isCookieSetForUsername(this.username)) {
              this.$refs.invisibleRecaptcha.execute();
            } else {
              this.handleLogin('1'); // recaptcha disabled, providing a random string to bypass validator
            }
          }
        });
    },
    loginCallback(isSuccess) {
      if (this.isRecaptchaEnabled()) {
        this.resetRecaptcha()
      }
      if (!isSuccess) {
        this.$nextTick(() => { this.$refs.password.$refs.input.focus() });
      }
    },
    handleLogin(token) {
      let loginObject = {
        usernameOrEmail: this.username,
        password: this.password,
        recaptchaToken: token,
        rememberMe: this.rememberMe,
        isAdminLogin: this.isAdminLogin,
        isIframe: this.type === 'iframe',
        callback: this.loginCallback
      }

      this.submitUserIdentity(loginObject);
    },
    showError(message) {
      this.error(message)
    },
    onExpired() {
      window.location.reload(true)
    },
    resetRecaptcha() {
      if (this.$refs.invisibleRecaptcha) {
        this.$refs.invisibleRecaptcha.reset()
      }
    },
    recaptchaSiteKey() {
      return config.recaptchaSiteKey;
    },
    isRecaptchaEnabled() {
      if (!config.recaptchaEnabled) {
        return false
      }

      return this.status.recaptchaRequired
    }
  },
  mounted: function () {
    this.config = config;
    if (config.showRememberMe && localStorage.getItem('loginForm.username')) {
      this.username =  localStorage.getItem('loginForm.username');
      this.rememberMe = true;
    }
    if (this.$route.query.username) {
      this.username = this.$route.query.username
      this.autofocus = 'password'
    }
    if (this.$route.query.type) {
      this.type = this.$route.query.type
    }
  },
  destroyed() {
  }
}
</script>